import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useWorkbench } from '../WorkbenchContext';

const API_BASE_URL = '/api';

function MyCases() {
  const {
    cases,
    activeCaseId,
    setActiveCaseId,
    activeCase,
    createCase,
    deleteCase,
    setActiveTab,
    linkReferenceToCase,
    addDocumentToCase,
    setCurrentRequirementsDocument,
    setArtifactTitle
  } = useWorkbench();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSamplesModal, setShowSamplesModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [sampleCases, setSampleCases] = useState([]);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [newCaseName, setNewCaseName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Load sample cases when modal opens
  useEffect(() => {
    if (showSamplesModal) {
      loadSampleCases();
    }
  }, [showSamplesModal]);

  const loadSampleCases = async () => {
    setLoadingSamples(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/sample-cases`, {
        withCredentials: true
      });
      setSampleCases(response.data.samples || []);
    } catch (error) {
      console.error('Error loading sample cases:', error);
    } finally {
      setLoadingSamples(false);
    }
  };

  const handleImportSample = async (sample) => {
    try {
      // Get sample content
      const response = await axios.get(`${API_BASE_URL}/sample-cases/${sample.id}`, {
        withCredentials: true
      });

      const sampleData = response.data;

      // Create a new case with the sample name
      const newCase = createCase({ name: sampleData.title });

      // Add the sample document as a document in the case
      addDocumentToCase(newCase.id, {
        id: `sample_${sample.id}`,
        filename: sampleData.filename,
        title: sampleData.title,
        content: sampleData.content,
        type: 'sample',
        addedAt: new Date().toISOString()
      });

      // Display the document immediately in the viewer
      setCurrentRequirementsDocument(sampleData.content);
      setArtifactTitle(sampleData.title);

      // Link related reference cases
      if (sample.related_reference_cases) {
        sample.related_reference_cases.forEach(refId => {
          linkReferenceToCase(newCase.id, refId);
        });
      }

      // Select the new case
      setActiveCaseId(newCase.id);
      setShowSamplesModal(false);

      // Go to Document Analysis
      setActiveTab('requirements');
    } catch (error) {
      console.error('Error importing sample case:', error);
      alert('Failed to import sample case');
    }
  };

  const handleCreateCase = (e) => {
    e.preventDefault();
    if (!newCaseName.trim()) return;

    const newCase = createCase({ name: newCaseName.trim() });
    setActiveCaseId(newCase.id);
    setNewCaseName('');
    setShowCreateModal(false);
  };

  const handleSelectCase = (caseId) => {
    setActiveCaseId(caseId);
  };

  const handleDeleteCase = (caseId, e) => {
    e.stopPropagation();
    if (window.confirm('Delete this case? This action cannot be undone.')) {
      deleteCase(caseId);
    }
  };

  const handleOpenCase = (caseId) => {
    setActiveCaseId(caseId);
    setActiveTab('requirements');
  };

  // Handle file upload to current case
  const handleUploadToCase = async (files) => {
    if (!activeCaseId || files.length === 0) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const response = await fetch(`${API_BASE_URL}/requirements/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Upload failed');

      const result = await response.json();

      // Add uploaded files as case documents
      result.uploaded.forEach(file => {
        addDocumentToCase(activeCaseId, {
          id: file.file_id,
          filename: file.filename,
          title: file.filename.replace(/\.[^/.]+$/, ''), // Remove extension for title
          content: file.content || `[Content from ${file.filename}]`,
          type: 'uploaded',
          size: file.size,
          addedAt: new Date().toISOString()
        });
      });

      setShowUploadModal(false);
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const filteredCases = cases.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="h-full flex flex-col bg-galaxy-950">
      {/* Header */}
      <div className="bg-galaxy-900/50 border-b border-galaxy-800/50 px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <span>&#9878;</span>
              My Cases
            </h2>
            <p className="text-galaxy-300 text-sm mt-1">
              Active matters and client cases
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowSamplesModal(true)}
              className="px-4 py-2 bg-galaxy-700 hover:bg-galaxy-600 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <span>&#128196;</span>
              Import Sample
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-galaxy-600 hover:bg-galaxy-500 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <span>+</span>
              New Case
            </button>
          </div>
        </div>

        {/* Active Case Banner */}
        {activeCase && (
          <div className="mt-4 bg-galaxy-600/20 border border-galaxy-600/40 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-galaxy-600 flex items-center justify-center">
                  <span className="text-white text-sm">&#9878;</span>
                </div>
                <div>
                  <div className="text-white font-medium">{activeCase.name}</div>
                  <div className="text-galaxy-400 text-xs">
                    Active Case - {activeCase.linkedReferences.length} references, {activeCase.documents.length} documents
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-3 py-1.5 bg-galaxy-700 hover:bg-galaxy-600 text-white text-sm rounded transition-colors flex items-center gap-1.5"
                >
                  <span>&#128228;</span>
                  Upload Documents
                </button>
                <button
                  onClick={() => setActiveTab('package-design')}
                  className="px-3 py-1.5 bg-galaxy-700 hover:bg-galaxy-600 text-white text-sm rounded transition-colors flex items-center gap-1.5"
                >
                  <span>&#128221;</span>
                  Create Template
                </button>
                <button
                  onClick={() => setActiveTab('requirements')}
                  className="px-3 py-1.5 bg-galaxy-600 hover:bg-galaxy-500 text-white text-sm rounded transition-colors"
                >
                  Open in Document Analysis
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Statistics */}
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="bg-galaxy-800/30 rounded-lg p-3">
            <div className="text-galaxy-400 text-xs uppercase">Total Cases</div>
            <div className="text-white text-2xl font-bold">{cases.length}</div>
          </div>
          <div className="bg-galaxy-800/30 rounded-lg p-3">
            <div className="text-galaxy-400 text-xs uppercase">Active Case</div>
            <div className="text-white text-lg font-bold truncate">
              {activeCase?.name || 'None Selected'}
            </div>
          </div>
          <div className="bg-galaxy-800/30 rounded-lg p-3">
            <div className="text-galaxy-400 text-xs uppercase">Total Documents</div>
            <div className="text-white text-2xl font-bold">
              {cases.reduce((sum, c) => sum + c.documents.length, 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-galaxy-900/30 border-b border-galaxy-800/50 px-6 py-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search cases..."
          className="w-full max-w-md px-4 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
        />
      </div>

      {/* Cases List */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {cases.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="text-6xl mb-4">&#9878;</div>
            <h3 className="text-xl text-white mb-2">No Cases Yet</h3>
            <p className="text-galaxy-400 mb-6 max-w-md">
              Create your first case to start organizing your legal work. Cases help you group related documents, reference materials, and AI-generated artifacts.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-galaxy-600 hover:bg-galaxy-500 text-white rounded-lg transition-colors"
            >
              Create Your First Case
            </button>
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-galaxy-400">No cases match your search</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCases.map(caseItem => (
              <div
                key={caseItem.id}
                onClick={() => handleOpenCase(caseItem.id)}
                className={`bg-galaxy-800/30 border rounded-lg p-4 cursor-pointer transition-all hover:bg-galaxy-800/50 ${
                  activeCaseId === caseItem.id
                    ? 'border-galaxy-500 bg-galaxy-800/50'
                    : 'border-galaxy-700/50 hover:border-galaxy-600/50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {activeCaseId === caseItem.id && (
                      <span className="px-2 py-0.5 bg-galaxy-600 text-white text-xs rounded-full">
                        Active
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveCaseId(caseItem.id);
                        setActiveTab('package-design');
                      }}
                      className="px-2 py-1 text-xs bg-galaxy-700/50 hover:bg-galaxy-600/50 text-galaxy-300 hover:text-white rounded transition-colors"
                      title="Create template"
                    >
                      &#128221;
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenCase(caseItem.id);
                      }}
                      className="px-2 py-1 text-xs bg-galaxy-700/50 hover:bg-galaxy-600/50 text-galaxy-300 hover:text-white rounded transition-colors"
                      title="Open case"
                    >
                      Open
                    </button>
                    <button
                      onClick={(e) => handleDeleteCase(caseItem.id, e)}
                      className="p-1 text-galaxy-500 hover:text-red-400 transition-colors"
                      title="Delete case"
                    >
                      &#x1F5D1;
                    </button>
                  </div>
                </div>

                <h3 className="text-white font-semibold text-lg mb-2">
                  {caseItem.name}
                </h3>

                <div className="space-y-1 text-sm text-galaxy-400">
                  <div className="flex items-center gap-2">
                    <span>&#128279;</span>
                    <span>{caseItem.linkedReferences.length} linked references</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>&#128196;</span>
                    <span>{caseItem.documents.length} documents</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>&#128230;</span>
                    <span>{caseItem.artifacts.length} artifacts</span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-galaxy-700/50 flex justify-between text-xs text-galaxy-500">
                  <span>Created {formatDate(caseItem.createdAt)}</span>
                  <span>Updated {formatDate(caseItem.updatedAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Case Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-galaxy-900 border border-galaxy-700 rounded-lg max-w-md w-full">
            <div className="border-b border-galaxy-700 px-6 py-4">
              <h2 className="text-xl font-bold text-white">Create New Case</h2>
              <p className="text-galaxy-400 text-sm mt-1">
                Start a new legal matter or client case
              </p>
            </div>

            <form onSubmit={handleCreateCase} className="px-6 py-4 space-y-4">
              <div>
                <label className="text-galaxy-300 text-sm mb-2 block">
                  Case Name *
                </label>
                <input
                  type="text"
                  value={newCaseName}
                  onChange={(e) => setNewCaseName(e.target.value)}
                  placeholder="e.g., Smith v. Jones"
                  className="w-full px-4 py-2 bg-galaxy-800/50 border border-galaxy-700 rounded-lg text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
                  autoFocus
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={!newCaseName.trim()}
                  className="flex-1 px-4 py-2 bg-galaxy-600 hover:bg-galaxy-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Case
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewCaseName('');
                  }}
                  className="px-4 py-2 bg-galaxy-700 hover:bg-galaxy-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sample Cases Modal */}
      {showSamplesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-galaxy-900 border border-galaxy-700 rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="border-b border-galaxy-700 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">Import Sample Case</h2>
                <p className="text-galaxy-400 text-sm mt-1">
                  Start with a pre-made case to explore the system
                </p>
              </div>
              <button
                onClick={() => setShowSamplesModal(false)}
                className="text-galaxy-400 hover:text-white transition-colors"
              >
                &#x2715;
              </button>
            </div>

            <div className="flex-1 overflow-auto px-6 py-4">
              {loadingSamples ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-galaxy-400">Loading samples...</div>
                </div>
              ) : sampleCases.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-galaxy-400">No sample cases available</div>
                </div>
              ) : (
                <div className="space-y-3">
                  {sampleCases.map(sample => (
                    <div
                      key={sample.id}
                      className="bg-galaxy-800/30 border border-galaxy-700/50 rounded-lg p-4 hover:bg-galaxy-800/50 transition-all"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="text-white font-semibold">{sample.title}</h3>
                          <p className="text-galaxy-400 text-sm mt-1 line-clamp-2">
                            {sample.description}
                          </p>
                          {sample.related_reference_cases && sample.related_reference_cases.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              <span className="text-galaxy-500 text-xs">Related precedents:</span>
                              {sample.related_reference_cases.map(refId => (
                                <span
                                  key={refId}
                                  className="px-2 py-0.5 bg-galaxy-700/50 text-galaxy-300 text-xs rounded"
                                >
                                  {refId.replace(/-/g, ' ').replace(/\d{4}$/, '').trim()}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleImportSample(sample)}
                          className="ml-4 px-4 py-2 bg-galaxy-600 hover:bg-galaxy-500 text-white text-sm rounded-lg transition-colors"
                        >
                          Import
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Documents Modal */}
      {showUploadModal && activeCase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-galaxy-900 border border-galaxy-700 rounded-lg max-w-lg w-full">
            <div className="border-b border-galaxy-700 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">Upload Documents</h2>
                <p className="text-galaxy-400 text-sm mt-1">
                  Add documents to "{activeCase.name}"
                </p>
              </div>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-galaxy-400 hover:text-white transition-colors"
              >
                &#x2715;
              </button>
            </div>

            <div className="px-6 py-6">
              {/* Drop zone */}
              <div
                className="border-2 border-dashed border-galaxy-600 rounded-lg p-8 text-center hover:border-galaxy-500 transition-colors"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-galaxy-400', 'bg-galaxy-800/30');
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('border-galaxy-400', 'bg-galaxy-800/30');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-galaxy-400', 'bg-galaxy-800/30');
                  const files = Array.from(e.dataTransfer.files);
                  if (files.length > 0) {
                    handleUploadToCase(files);
                  }
                }}
              >
                <div className="text-4xl mb-4">&#128230;</div>
                <p className="text-galaxy-300 mb-2">
                  {isUploading ? 'Uploading...' : 'Drag & drop files here'}
                </p>
                <p className="text-galaxy-500 text-sm mb-4">
                  PDF, Excel, PowerPoint, or text files
                </p>
                <label className="inline-block">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.xlsx,.xls,.pptx,.ppt,.doc,.docx,.txt,.md"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        handleUploadToCase(files);
                      }
                    }}
                    className="hidden"
                    disabled={isUploading}
                  />
                  <span className={`px-4 py-2 bg-galaxy-600 hover:bg-galaxy-500 text-white rounded-lg cursor-pointer transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    {isUploading ? 'Uploading...' : 'Browse Files'}
                  </span>
                </label>
              </div>

              {/* Current documents in case */}
              {activeCase.documents.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-galaxy-400 mb-2">
                    Current Documents ({activeCase.documents.length})
                  </h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {activeCase.documents.map(doc => (
                      <div key={doc.id} className="flex items-center gap-2 px-3 py-2 bg-galaxy-800/30 rounded text-sm">
                        <span>
                          {doc.type === 'sample' ? '\u{1F4C4}' : doc.type === 'uploaded' ? '\u{1F4CE}' : '\u{1F4DD}'}
                        </span>
                        <span className="text-galaxy-300 truncate flex-1">
                          {doc.title || doc.filename}
                        </span>
                        <span className="text-galaxy-500 text-xs">
                          {doc.type}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-galaxy-700 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 bg-galaxy-700 hover:bg-galaxy-600 text-white rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyCases;
